import { t } from 'i18next';
import { ChatSessionFile } from 'models/Message';
import { DATE_FORMAT_DAY_SHORT_MONTH, getFormattedDate } from 'dateUtils';
import utils from 'utils';

/** how many of the most recent files the sidebar section previews */
export const PREVIEWED_FILES_COUNT = 3;

const FILE_TYPE_LABEL_KEYS = {
  image: 'fileTypeImage',
  video: 'fileTypeVideo',
  word: 'fileTypeDocument',
  excel: 'fileTypeSpreadsheet',
  pdf: 'fileTypePdf',
  ppt: 'fileTypePresentation',
  default: 'fileTypeFile',
} as const;

const APPLICATION_SUFFIXES: Record<string, keyof typeof FILE_TYPE_LABEL_KEYS> =
  {
    msword: 'word',
    'vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
    pdf: 'pdf',
    'vnd.ms-powerpoint': 'ppt',
    'vnd.openxmlformats-officedocument.presentationml.presentation': 'ppt',
    'vnd.ms-excel': 'excel',
    'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
  };

const PREFIX_DETERMINED_TYPES = ['image', 'video'];

/**
 * A readable name for the file's kind ("PDF", "Spreadsheet") — the raw mime type is far
 * too long to sit on a row, and the extension alone reads as noise next to the file name.
 */
export const getFileTypeLabel = (type = '') => {
  const [prefix, suffix] = type.split('/');

  if (PREFIX_DETERMINED_TYPES.includes(prefix))
    return t(FILE_TYPE_LABEL_KEYS[prefix as keyof typeof FILE_TYPE_LABEL_KEYS]);

  if (prefix === 'application' && APPLICATION_SUFFIXES[suffix])
    return t(FILE_TYPE_LABEL_KEYS[APPLICATION_SUFFIXES[suffix]]);

  return t(FILE_TYPE_LABEL_KEYS.default);
};

/**
 * "PDF, 14 Aug" in the sidebar, "PDF, 2.4 MB, 14 Aug" in the modal — each part is dropped
 * when the file doesn't carry it.
 */
export const getFileMetaLine = (
  { file, createdAt }: ChatSessionFile,
  withSize?: boolean,
) => {
  const { type, originalType, size } = file || {};

  return [
    getFileTypeLabel(originalType || type),
    withSize ? utils.humanizeFileSize(size) : '',
    createdAt
      ? getFormattedDate(new Date(createdAt), DATE_FORMAT_DAY_SHORT_MONTH)
      : '',
  ]
    .filter(Boolean)
    .join(', ');
};
